// Types.
import type event from '../Types/Event';
import type length from '../Types/Length';
import type source from '../Types/Source';
import type { state } from '../Types/State';
import type { markerCallback, sceneCallback } from '../Types/Callbacks';

// Constants.
import { directions } from '../Types/Direction';
import { states } from '../Types/State';

// Interfaces.
import { SceneOptions } from '../Interfaces/Options';

// Classes.
import Handler from './Handler';
import Listener from "./Listener";
import Marker from './Scene/Marker';
import Progress from './Progress';
import Section from './Scene/Section';

/**
 * Scrolly.Scene
 *
 * @author: FelixKiesewetter <info@felixkiesewetter.de>
 * @lastModified: 2026-06-16 Improvements.
 * @package felixkiesewetter-js/scroll
 * @version: 2.1.0
 */
class Scene extends Handler<SceneOptions>
{

    private lastProgress ?:number;
    private markers      :Map<string, Marker> = new Map();
    private progress     :Progress;
    private sections     :Map<string, Section> = new Map();
    private state        :state = states.BEFORE;

    /**
     * Constructor.
     */
    constructor(source :source, options :SceneOptions) {
        // TODO start: maybe all handlers should have setNode()
        super(options);
        this.setNode(source);
        // TODO end.

        // Observe node scroll progress.
        this.progress = Progress.observe(source, {
            dataset: this.options.dataset,
            debug: this.options.debug,
            onChange: (args :{ progress :number, direction :number, distance :number }) :void => {
                if (0 === args.progress && directions.UP === args.direction && states.BEFORE !== this.getState()) {
                    this.setState(states.BEFORE);
                    return;
                }
                if (1 >= args.progress && directions.DOWN === args.direction) {
                    this.options.onStart && this.options.onStart(this.getCallbackArguments());
                }
                if (99 < args.progress && directions.DOWN === args.direction) {
                    this.options.onEnd && this.options.onEnd(this.getCallbackArguments());
                }
                if (100 === args.progress && directions.DOWN === args.direction && states.AFTER !== this.getState()) {
                    this.setState(states.AFTER);
                    return;
                }
                if (this.getState() === states.WHILE) {
                    return;
                }
                this.setState(states.WHILE);
            },
            thresholdTop: this.options.thresholdTop,
            thresholdBottom: this.options.thresholdBottom,
        });

        // Make sure, sticky works with long timelines,
        // the sticky element should never exceed the current viewport height,
        // or else the browser will continue scrolling too early!
        //if (this.isSticky()) {
        //    this.props.node.style.position = 'relative';
        //    this.props.node.style.overflow = 'visible';
        //    const stickyElement: HTMLDivElement = document.createElement('div');
        //    this.props.node
        //}
    }

    // ################
    // Abstract methods
    // ################

    /**
     * Define default settings.
     */
    public defaults() :SceneOptions
    {
        return {
            dataset: false,
            debug: false,
            thresholdTop: 0,
            thresholdBottom: 0,
            onScroll: null,
            onStart: null,
            onEnd: null,
            onEnter: null,
            onExit: null,
        };
    }

    /**
     * This callback listens to the scroll event.
     */
    public events() :event[]
    {
        return [ 'init', 'scroll', 'resize' ];
    }

    /**
     * Check if an animation frame should be requested or not.
     */
    public requestAnimationFrames(event :Event, handler :Listener) :Function[]
    {
        let animationFrames :Function[] = [];

        if (
            this.progress.get() === this.getLastProgress() ||
            !this.progress.isActive()

        ) {
            return animationFrames;
        }

        animationFrames.push(() :void => {
            this.options.onScroll && this.options.onScroll(this.getCallbackArguments());
        });

        if (!this.markers.size) {
            return animationFrames;
        }

        this.markers.forEach((marker :Marker, id :string) :void => {
            if (marker.changedState(this.progress)) {
                animationFrames.push(() :void => {
                    marker.callOnStateChange();
                });
            }
        });

        return animationFrames;
    }

    // ##############
    // Public methods
    // ##############

    /**
     * Create a single marker at a given position and assign a callback function whenever its state changes.
     */
    public at(at :length, onStateChange :markerCallback) :Scene
    {
        this.markers.set(
            `marker-${this.markers.size}`,
            Marker.create({
                at: at,
                id: `marker-${this.markers.size}`,
                onStateChange: onStateChange,
            })
        );
        return this;
    }

    public between(from :length, to :length, onScroll :sectionCallback) :Scene
    {
        // TODO: Create two markers an from and to and connect them to a Section somehow
        return this;
    }

    public from(from :length, onScroll :sectionCallback) :Scene
    {
        this.between(from, '100%', onScroll);
        return this;
    }

    public to(to :length, onScroll :sectionCallback) :Scene
    {
        this.between('0%', to, onScroll);
        return this;
    }

    // #######
    // Getters
    // #######

    /**
     * Getter for all callback arguments.
     */
    private getCallbackArguments() :{}
    {
        return {
            progress: this.getProgress(),
            ratio: this.getRatio(),
            distance: this.getDistance(),
            state: this.getState(),
        }
    }

    /**
     * Getter for the scrolled distance (px).
     */
    public getDistance() :number
    {
        return this.progress.getDistance();
    }

    /**
     * Getter for the last stored progress (%).
     */
    private getLastProgress() :undefined|number
    {
        return this.lastProgress;
    }

    /**
     * Getter for the current progress (%).
     */
    public getProgress() :number
    {
        return this.progress.get();
    }

    /**
     * Getter for the current ratio (0-1).
     */
    public getRatio() :number
    {
        return this.progress.getRatio();
    }

    /**
     * Getter for the current state.
     */
    public getState() :state
    {
        return this.state;
    }

    // #######
    // Setters
    // #######

    /**
     * Setter for the onEnd callback.
     */
    public onEnd(onEnd :sceneCallback) :Scene
    {
        this.options.onEnd = onEnd;
        return this;
    }

    /**
     * Setter for the onEnter callback.
     */
    public onEnter(onEnter :sceneCallback) :Scene
    {
        this.options.onEnter = onEnter;
        return this;
    }

    /**
     * Setter for the onExit callback.
     */
    public onExit(onExit :sceneCallback) :Scene
    {
        this.options.onExit = onExit;
        return this;
    }

    /**
     * Setter for the onSrcoll callback.
     */
    public onScroll(onScroll :sceneCallback) :Scene
    {
        this.options.onScroll = onScroll;
        return this;
    }

    /**
     * Setter for the onStart callback.
     */
    public onStart(onStart :sceneCallback) :Scene
    {
        this.options.onStart = onStart;
        return this;
    }

    /**
     * Private setter for the current state, also sets the data attribute.
     */
    private setState(state :state) :Scene
    {
        if (state === this.state) {
            return this;
        }
        this.state = state;
        this.getNode().dataset.scrollyScene = state.toString();
        if (states.WHILE === state) {
            this.options.onEnter && this.options.onEnter(this.getCallbackArguments());
        }
        else {
            this.options.onExit && this.options.onExit(this.getCallbackArguments());
        }
        return this;
    }

    // ##############
    // Static methods
    // ##############

    /**
     * Create a scene from a node.
     */
    static create(source :source, options :SceneOptions)
    {
        return new Scene(source, options);
    }

}

export default Scene;